import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface KPIMetrics {
  dau: number;
  mau: number;
  recipeSaves: {
    average: number;
    total: number;
    userCount: number;
  };
  completionRate: {
    completionRate: number;
    totalViews: number;
    totalCompletions: number;
  };
  shares: {
    total: number;
    byPlatform: Record<string, number>;
  };
  retention: {
    totalSignups: number;
    sevenDayRetention: number;
    thirtyDayRetention: number;
  };
  timeOnRecipe: {
    averageTime: number;
    medianTime: number;
    totalSessions: number;
  };
}

const COLORS = ['#2563eb', '#059669', '#7c3aed', '#dc2626', '#ea580c', '#0891b2', '#ca8a04', '#db2777'];
const CHART_MARGIN = { top: 16, right: 16, left: 16, bottom: 80 };

export default function KPIMetrics() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [metrics, setMetrics] = useState<KPIMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadMetrics();
  }, [dateRange]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
      }

      const metricsResponse = await api.get('/admin/metrics/all', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      });
      const raw = metricsResponse.data?.data;
      setMetrics({
        dau: typeof raw?.dau === 'number' ? raw.dau : 0,
        mau: typeof raw?.mau === 'number' ? raw.mau : 0,
        recipeSaves: {
          average: typeof raw?.recipeSaves?.average === 'number' ? raw.recipeSaves.average : 0,
          total: typeof raw?.recipeSaves?.total === 'number' ? raw.recipeSaves.total : 0,
          userCount: typeof raw?.recipeSaves?.userCount === 'number' ? raw.recipeSaves.userCount : 0,
        },
        completionRate: {
          completionRate: typeof raw?.completionRate?.completionRate === 'number' ? raw.completionRate.completionRate : 0,
          totalViews: typeof raw?.completionRate?.totalViews === 'number' ? raw.completionRate.totalViews : 0,
          totalCompletions: typeof raw?.completionRate?.totalCompletions === 'number' ? raw.completionRate.totalCompletions : 0,
        },
        shares: {
          total: typeof raw?.shares?.total === 'number' ? raw.shares.total : 0,
          byPlatform: raw?.shares?.byPlatform && typeof raw.shares.byPlatform === 'object' ? raw.shares.byPlatform : {},
        },
        retention: {
          totalSignups: typeof raw?.retention?.totalSignups === 'number' ? raw.retention.totalSignups : 0,
          sevenDayRetention: typeof raw?.retention?.sevenDayRetention === 'number' ? raw.retention.sevenDayRetention : 0,
          thirtyDayRetention: typeof raw?.retention?.thirtyDayRetention === 'number' ? raw.retention.thirtyDayRetention : 0,
        },
        timeOnRecipe: {
          averageTime: typeof raw?.timeOnRecipe?.averageTime === 'number' ? raw.timeOnRecipe.averageTime : 0,
          medianTime: typeof raw?.timeOnRecipe?.medianTime === 'number' ? raw.timeOnRecipe.medianTime : 0,
          totalSessions: typeof raw?.timeOnRecipe?.totalSessions === 'number' ? raw.timeOnRecipe.totalSessions : 0,
        },
      });
    } catch (error) {
      console.error('Error loading KPI metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('kpi.loading')}</p>
      </div>
    );
  }

  if (!metrics) {
    return <div className="text-center py-8 text-red-600">{t('kpi.failedToLoad')}</div>;
  }

  const hasAnyData =
    metrics.dau > 0 ||
    metrics.mau > 0 ||
    metrics.recipeSaves.total > 0 ||
    metrics.completionRate.totalViews > 0 ||
    metrics.shares.total > 0 ||
    metrics.retention.totalSignups > 0 ||
    metrics.timeOnRecipe.totalSessions > 0;

  // Prepare data for charts
  const sharePlatformData = Object.entries(metrics.shares.byPlatform || {}).map(([platform, count]) => ({
    name: platform.charAt(0).toUpperCase() + platform.slice(1),
    value: count,
  }));

  const retentionData = [
    { name: t('kpi.retention7d'), value: metrics.retention?.sevenDayRetention ?? 0 },
    { name: t('kpi.retention30d'), value: metrics.retention?.thirtyDayRetention ?? 0 },
  ];

  const engagementData = [
    { name: t('kpi.recipeSavesLabel'), value: metrics.recipeSaves?.total ?? 0 },
    { name: t('kpi.recipeViews'), value: metrics.completionRate?.totalViews ?? 0 },
    { name: t('kpi.recipeCompletions'), value: metrics.completionRate?.totalCompletions ?? 0 },
    { name: t('kpi.totalShares'), value: metrics.shares?.total ?? 0 },
  ];

  return (
    <div className={`space-y-6 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {!hasAnyData && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-amber-800">
          <p className="font-medium">{t('kpi.noData')}</p>
          <p className="text-sm mt-1">{t('kpi.noDataHint')}</p>
        </div>
      )}
      <div className={`flex gap-2 mb-4 ${isRtl ? 'justify-start flex-row-reverse' : 'justify-end'}`}>
        <button
          onClick={() => setDateRange('7d')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${dateRange === '7d' ? 'bg-primary-orange text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          {t('kpi.last7Days')}
        </button>
        <button
          onClick={() => setDateRange('30d')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${dateRange === '30d' ? 'bg-primary-orange text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          {t('kpi.last30Days')}
        </button>
        <button
          onClick={() => setDateRange('90d')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${dateRange === '90d' ? 'bg-primary-orange text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          {t('kpi.last90Days')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500 rtl:border-l-0 rtl:border-r-4 rtl:border-blue-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.dailyActiveUsers')}</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{metrics.dau ?? 0}</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.today')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500 rtl:border-l-0 rtl:border-r-4 rtl:border-purple-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.monthlyActiveUsers')}</div>
          <div className="text-3xl font-bold text-purple-600 mt-2">{metrics.mau ?? 0}</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.thisMonth')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500 rtl:border-l-0 rtl:border-r-4 rtl:border-green-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.recipeSaves')}</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{metrics.recipeSaves?.total ?? 0}</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.avgPerUser', { value: (metrics.recipeSaves?.average ?? 0).toFixed(1) })}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500 rtl:border-l-0 rtl:border-r-4 rtl:border-orange-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.completionRate')}</div>
          <div className="text-3xl font-bold text-orange-600 mt-2">{(metrics.completionRate?.completionRate ?? 0).toFixed(1)}%</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.views', { completions: metrics.completionRate?.totalCompletions ?? 0, views: metrics.completionRate?.totalViews ?? 0 })}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500 rtl:border-l-0 rtl:border-r-4 rtl:border-red-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.totalShares')}</div>
          <div className="text-3xl font-bold text-red-600 mt-2">{metrics.shares?.total ?? 0}</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.allPlatforms')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500 rtl:border-l-0 rtl:border-r-4 rtl:border-indigo-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.newSignups')}</div>
          <div className="text-3xl font-bold text-indigo-600 mt-2">{metrics.retention?.totalSignups ?? 0}</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.inSelectedPeriod')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-teal-500 rtl:border-l-0 rtl:border-r-4 rtl:border-teal-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.sevenDayRetention')}</div>
          <div className="text-3xl font-bold text-teal-600 mt-2">{(metrics.retention?.sevenDayRetention ?? 0).toFixed(1)}%</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.userRetentionRate')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-pink-500 rtl:border-l-0 rtl:border-r-4 rtl:border-pink-500">
          <div className="text-gray-600 text-sm font-medium">{t('kpi.thirtyDayRetention')}</div>
          <div className="text-3xl font-bold text-pink-600 mt-2">{(metrics.retention?.thirtyDayRetention ?? 0).toFixed(1)}%</div>
          <div className="text-sm text-gray-500 mt-1">{t('kpi.userRetentionRate')}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-black mb-4">{t('kpi.timeOnRecipe')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-orange">{Math.round((metrics.timeOnRecipe?.averageTime ?? 0) / 60)} {t('kpi.min')}</div>
            <div className="text-sm text-gray-600 mt-1">{t('kpi.averageTime')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-orange">{Math.round((metrics.timeOnRecipe?.medianTime ?? 0) / 60)} {t('kpi.min')}</div>
            <div className="text-sm text-gray-600 mt-1">{t('kpi.medianTime')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-orange">{metrics.timeOnRecipe?.totalSessions ?? 0}</div>
            <div className="text-sm text-gray-600 mt-1">{t('kpi.totalSessions')}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sharePlatformData.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-black mb-4">{t('kpi.sharesByPlatform')}</h3>
            <div dir="ltr" className="min-h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={CHART_MARGIN}>
                  <Pie
                    data={sharePlatformData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sharePlatformData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('kpi.userRetentionRates')}</h3>
          <div dir="ltr" className="min-h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={retentionData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                <Bar dataKey="value" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('kpi.engagementMetrics')}</h3>
          <div dir="ltr" className="min-h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={engagementData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                <Bar dataKey="value" fill="#059669" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('kpi.recipeSavesStats')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700 font-medium">{t('kpi.totalSaves')}</span>
              <span className="text-2xl font-bold text-primary-orange">{metrics.recipeSaves?.total ?? 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700 font-medium">{t('kpi.activeUsers')}</span>
              <span className="text-2xl font-bold text-primary-orange">{metrics.recipeSaves?.userCount ?? 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700 font-medium">{t('kpi.averagePerUser')}</span>
              <span className="text-2xl font-bold text-primary-orange">{(metrics.recipeSaves?.average ?? 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-black mb-4">{t('kpi.seoMetrics')}</h3>
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">{t('kpi.seoPlaceholder')}</p>
        </div>
      </div>
    </div>
  );
}
